<template>
  <div class="sidebar-overlay" v-if="showSidebar" @click="toggleSidebar()"></div>
  <nav class="sidebar" :class="{ 'sidebar-visible': showSidebar, 'sidebar-hidden': !showSidebar }">
  <div style="overflow-y: auto;">
    <ul class="nav flex-column">
      
      <!-- Clocked In status -->
      <li class="nav-item position-relative pt-3">
        <a class="nav-link py-0 -ps-1 clocked" href="#" @click.prevent="openClockModal">
          <span>
            <Clock :size="20" class="position-absolute mt-1" :class="isClockedIn ? 'text-success' : 'not-clocked'" />
            <span class="d-inline-block ms-4 ps-2" :class="isClockedIn ? 'text-success' : 'not-clocked'">{{ clockStatusText }}</span>
            <span v-if="isClockedIn && formattedElapsedTime" class="badge bg-success-subtle text-success ms-2">{{ formattedElapsedTime }}</span>
          </span>
        </a>
      </li>

      <!-- Dashboard -->
      <li class="nav-item position-relative">
        <router-link class="nav-link py-0 -ps-1" to="/home">
          <span>
            <LayoutDashboard :size="20" class="position-absolute mt-1" />
            <span class="d-inline-block ms-4 ps-2">Dashboard</span>
          </span>
        </router-link>
      </li>
      
      <!-- Quick Actions -->
      <li class="nav-item position-relative">
        <a class="nav-link py-0 -ps-1" href="#" @click.prevent="openRequestModal()">
          <span>
            <PlusCircle :size="20" class="position-absolute mt-1" />
            <span class="d-inline-block ms-4 ps-2">New Request</span>
          </span>
        </a>
      </li>
      <li class="nav-item position-relative">
        <a class="nav-link py-0 -ps-1" href="#" @click.prevent="openAssignmentModal()">
          <span>
            <CheckSquare :size="20" class="position-absolute mt-1" />
            <span class="d-inline-block ms-4 ps-2">New Task</span>
          </span>
        </a>
      </li>
      <li class="nav-item position-relative">
        <router-link class="nav-link py-0 -ps-1" to="/pm/new">
          <span>
            <ClipboardPlus :size="20" class="position-absolute mt-1" />
            <span class="d-inline-block ms-4 ps-2">New PM</span>
          </span>
        </router-link>
      </li>

      <!-- Notifications + Messages -->
      <li class="nav-item position-relative">
        <router-link class="nav-link py-0 -ps-1" to="/notification/list">
          <span>
            <Bell :size="20" class="position-absolute mt-1" />
            <span class="d-inline-block ms-4 ps-2">Notifications</span>
            <span v-if="unreadCount > 0" class="badge bg-danger ms-2">{{ unreadCount }}</span>
          </span>
        </router-link>
      </li>
      <li class="nav-item position-relative">
        <a class="nav-link py-0 -ps-1 disabled-link" href="#" @click.prevent>
          <span>
            <MessageSquare :size="20" class="position-absolute mt-1" />
            <span class="d-inline-block ms-4 ps-2">Messages</span>
          </span>
        </a>
      </li>

      <li class="nav-item position-relative">
        <router-link class="nav-link py-0 -ps-1" to="/clock/list">
          <span>
            <Clock :size="20" class="position-absolute mt-1" />
            <span class="d-inline-block ms-4 ps-2">Time Tracker</span>
          </span>
        </router-link>
      </li>
      
      <li><hr /></li>

      <!-- Work Flow Accordion -->
      <li class="nav-item position-relative">
        <a class="nav-link py-0 -ps-1 accordion-toggle" href="#" @click.prevent="toggleAccordion('workflow')">
          <span>
            <Workflow :size="20" class="position-absolute mt-1" />
            <span class="d-inline-block ms-4 ps-2">Work Flow</span>
            <ChevronRight :size="16" class="accordion-chevron" :class="{ 'rotated': openAccordion === 'workflow' }" />
          </span>
        </a>
        <ul class="nav flex-column accordion-content" :class="{ 'open': openAccordion === 'workflow' }">
          <li class="nav-item position-relative">
            <router-link class="nav-link py-0 -ps-1 sub-item" to="/request/list">
              <span>
                <ClipboardList :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">Requests</span>
              </span>
            </router-link>
          </li>
          <li class="nav-item position-relative">
            <router-link class="nav-link py-0 -ps-1 sub-item" to="/job/list">
              <span>
                <FileText :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">Workorders</span>
              </span>
            </router-link>
          </li>
          <li class="nav-item position-relative">
            <router-link class="nav-link py-0 -ps-1 sub-item" to="/project/status">
              <span>
                <Layers :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">Project Status</span>
              </span>
            </router-link>
          </li>
          <li class="nav-item position-relative">
            <router-link class="nav-link py-0 -ps-1 sub-item" to="/estimates">
              <span>
                <FileText :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">Estimates</span>
              </span>
            </router-link>
          </li>
          <li class="nav-item position-relative">
            <router-link class="nav-link py-0 -ps-1 sub-item" to="/purchase-orders">
              <span>
                <ShoppingCart :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">Purchase Orders</span>
              </span>
            </router-link>
          </li>
          <li class="nav-item position-relative">
            <router-link class="nav-link py-0 -ps-1 sub-item" to="/workorders">
              <span>
                <Receipt :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">Invoices</span>
              </span>
            </router-link>
          </li>
        </ul>
      </li>

      <!-- Work Flow Management Accordion -->
      <li class="nav-item position-relative">
        <a class="nav-link py-0 -ps-1 accordion-toggle" href="#" @click.prevent="toggleAccordion('workflowmgmt')">
          <span>
            <FolderKanban :size="20" class="position-absolute mt-1" />
            <span class="d-inline-block ms-4 ps-2">Work Flow Management</span>
            <ChevronRight :size="16" class="accordion-chevron" :class="{ 'rotated': openAccordion === 'workflowmgmt' }" />
          </span>
        </a>
        <ul class="nav flex-column accordion-content" :class="{ 'open': openAccordion === 'workflowmgmt' }">
          <li class="nav-item position-relative">
            <router-link class="nav-link py-0 -ps-1 sub-item" to="/request/status">
              <span>
                <ListOrdered :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">Request Management</span>
              </span>
            </router-link>
          </li>
          <li v-if="isAdmin" class="nav-item position-relative">
            <router-link class="nav-link py-0 -ps-1 sub-item" to="/tech/list">
              <span>
                <Users :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">Technician Management</span>
              </span>
            </router-link>
          </li>
          <li class="nav-item position-relative">
            <router-link class="nav-link py-0 -ps-1 sub-item" to="/home">
              <span>
                <BarChart2 :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">Work Review</span>
              </span>
            </router-link>
          </li>
          <li class="nav-item position-relative">
            <router-link class="nav-link py-0 -ps-1 sub-item" to="/service-reports">
              <span>
                <ClipboardCheck :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">Service Reports</span>
              </span>
            </router-link>
          </li>
          <li class="nav-item position-relative">
            <router-link class="nav-link py-0 -ps-1 sub-item" to="/calendar">
              <span>
                <Calendar :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">Calendars / Schedules</span>
              </span>
            </router-link>
          </li>
          <li class="nav-item position-relative">
            <router-link class="nav-link py-0 -ps-1 sub-item" to="/pm/list">
              <span>
                <Wrench :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">PM's</span>
              </span>
            </router-link>
          </li>
        </ul>
      </li>

      <!-- Quick Views Accordion -->
      <li class="nav-item position-relative">
        <a class="nav-link py-0 -ps-1 accordion-toggle" href="#" @click.prevent="toggleAccordion('quickviews')">
          <span>
            <Zap :size="20" class="position-absolute mt-1" />
            <span class="d-inline-block ms-4 ps-2">Quick Views</span>
            <ChevronRight :size="16" class="accordion-chevron" :class="{ 'rotated': openAccordion === 'quickviews' }" />
          </span>
        </a>
        <ul class="nav flex-column accordion-content" :class="{ 'open': openAccordion === 'quickviews' }">
          <li class="nav-item position-relative">
            <router-link class="nav-link py-0 -ps-1 sub-item" :to="{ path: '/job/list', query: { status: 2, department: 'IT/Web', page: 0 } }">
              <span>
                <CheckCircle :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">Work Complete IT/Web</span>
              </span>
            </router-link>
          </li>
          <li class="nav-item position-relative">
            <router-link class="nav-link py-0 -ps-1 sub-item" :to="{ path: '/job/list', query: { status: 2, department: 'Industrial/Commercial', page: 0 } }">
              <span>
                <CheckCircle :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">Work Complete Ind/Comm</span>
              </span>
            </router-link>
          </li>
          <li class="nav-item position-relative">
            <router-link class="nav-link py-0 -ps-1 sub-item" :to="{ path: '/job/list', query: { status: 10, page: 0 } }">
              <span>
                <Clock :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">Awaiting PO</span>
              </span>
            </router-link>
          </li>
          <li class="nav-item position-relative">
            <router-link v-if="jobStatusLink('In Review')" class="nav-link py-0 -ps-1 sub-item" :to="jobStatusLink('In Review')">
              <span>
                <Eye :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">In Review</span>
              </span>
            </router-link>
            <a v-else class="nav-link py-0 -ps-1 sub-item disabled-link" href="#" @click.prevent>
              <span>
                <Eye :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">In Review</span>
              </span>
            </a>
          </li>
          <li class="nav-item position-relative">
            <router-link class="nav-link py-0 -ps-1 sub-item" :to="{ path: '/job/list', query: { status: 11, page: 0 } }">
              <span>
                <Headphones :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">Customer Support</span>
              </span>
            </router-link>
          </li>
          <li class="nav-item position-relative">
            <router-link class="nav-link py-0 -ps-1 sub-item" :to="{ path: '/job/list', query: { status: 12, page: 0 } }">
              <span>
                <Settings :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">Maintenance</span>
              </span>
            </router-link>
          </li>
          <li class="nav-item position-relative">
            <router-link class="nav-link py-0 -ps-1 sub-item" :to="{ path: '/job/list', query: { status: 19, page: 0 } }">
              <span>
                <RefreshCw :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">Renewals</span>
              </span>
            </router-link>
          </li>
          <li class="nav-item position-relative">
            <router-link v-if="jobStatusLink('Billing Hold')" class="nav-link py-0 -ps-1 sub-item" :to="jobStatusLink('Billing Hold')">
              <span>
                <Ban :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">Billing Hold</span>
              </span>
            </router-link>
            <a v-else class="nav-link py-0 -ps-1 sub-item disabled-link" href="#" @click.prevent>
              <span>
                <Ban :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">Billing Hold</span>
              </span>
            </a>
          </li>
          <li class="nav-item position-relative">
            <router-link v-if="jobStatusLink('Lost Revenue')" class="nav-link py-0 -ps-1 sub-item" :to="jobStatusLink('Lost Revenue')">
              <span>
                <TrendingDown :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">Lost Revenue</span>
              </span>
            </router-link>
            <a v-else class="nav-link py-0 -ps-1 sub-item disabled-link" href="#" @click.prevent>
              <span>
                <TrendingDown :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">Lost Revenue</span>
              </span>
            </a>
          </li>
          <li class="nav-item position-relative">
            <router-link class="nav-link py-0 -ps-1 sub-item" to="/pm/list">
              <span>
                <Repeat :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">Recurring</span>
              </span>
            </router-link>
          </li>
        </ul>
      </li>
      
      <li><hr /></li>
      
      <!-- Document Center Accordion -->
      <li class="nav-item position-relative">
        <a class="nav-link py-0 -ps-1 accordion-toggle" href="#" @click.prevent="toggleAccordion('documents')">
          <span>
            <FileStack :size="20" class="position-absolute mt-1" />
            <span class="d-inline-block ms-4 ps-2">Document Center</span>
            <ChevronRight :size="16" class="accordion-chevron" :class="{ 'rotated': openAccordion === 'documents' }" />
          </span>
        </a>
        <ul class="nav flex-column accordion-content" :class="{ 'open': openAccordion === 'documents' }">
          <li class="nav-item position-relative">
            <router-link class="nav-link py-0 -ps-1 sub-item" to="/procedures">
              <span>
                <ListChecks :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">Procedures and Checklists</span>
              </span>
            </router-link>
          </li>
          <li class="nav-item position-relative">
            <router-link class="nav-link py-0 -ps-1 sub-item" to="/forms">
              <span>
                <FileEdit :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">Form Templates</span>
              </span>
            </router-link>
          </li>
          <li class="nav-item position-relative">
            <router-link class="nav-link py-0 -ps-1 sub-item" to="/files">
              <span>
                <FolderOpen :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">Client Documents</span>
              </span>
            </router-link>
          </li>
        </ul>
      </li>

      <!-- Company Center Accordion -->
      <li class="nav-item position-relative">
        <a class="nav-link py-0 -ps-1 accordion-toggle" href="#" @click.prevent="toggleAccordion('company')">
          <span>
            <Building :size="20" class="position-absolute mt-1" />
            <span class="d-inline-block ms-4 ps-2">Company Center</span>
            <ChevronRight :size="16" class="accordion-chevron" :class="{ 'rotated': openAccordion === 'company' }" />
          </span>
        </a>
        <ul class="nav flex-column accordion-content" :class="{ 'open': openAccordion === 'company' }">
          <li class="nav-item position-relative">
            <router-link class="nav-link py-0 -ps-1 sub-item" to="/client/list">
              <span>
                <Building :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">Customers</span>
              </span>
            </router-link>
          </li>
          <li class="nav-item position-relative">
            <a class="nav-link py-0 -ps-1 sub-item disabled-link" href="#" @click.prevent>
              <span>
                <Car :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">Vehicles</span>
              </span>
            </a>
          </li>
          <li class="nav-item position-relative">
            <router-link class="nav-link py-0 -ps-1 sub-item" to="/vendors">
              <span>
                <Truck :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">Vendors</span>
              </span>
            </router-link>
          </li>
          <li class="nav-item position-relative">
            <router-link class="nav-link py-0 -ps-1 sub-item" to="/vendors">
              <span>
                <UserCheck :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">Subcontractors</span>
              </span>
            </router-link>
          </li>
          <li class="nav-item position-relative">
            <router-link class="nav-link py-0 -ps-1 sub-item" to="/files">
              <span>
                <FolderOpen :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">Company Documents</span>
              </span>
            </router-link>
          </li>
          <li class="nav-item position-relative">
            <router-link class="nav-link py-0 -ps-1 sub-item" to="/part/list">
              <span>
                <Package :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">Parts and Inventory</span>
              </span>
            </router-link>
          </li>
          <li class="nav-item position-relative">
            <router-link class="nav-link py-0 -ps-1 sub-item" to="/expense/list">
              <span>
                <Banknote :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">Expenses</span>
              </span>
            </router-link>
          </li>
          <li class="nav-item position-relative">
            <router-link class="nav-link py-0 -ps-1 sub-item" to="/mileage/list">
              <span>
                <MapPin :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">Mileage</span>
              </span>
            </router-link>
          </li>
        </ul>
      </li>
      
      <li><hr /></li>
      
      <!-- Administrative Accordion (Admin only) -->
      <li v-if="isAdmin" class="nav-item position-relative">
        <a class="nav-link py-0 -ps-1 accordion-toggle" href="#" @click.prevent="toggleAccordion('administrative')">
          <span>
            <Settings :size="20" class="position-absolute mt-1" />
            <span class="d-inline-block ms-4 ps-2">Administrative</span>
            <ChevronRight :size="16" class="accordion-chevron" :class="{ 'rotated': openAccordion === 'administrative' }" />
          </span>
        </a>
        <ul class="nav flex-column accordion-content" :class="{ 'open': openAccordion === 'administrative' }">
          <li class="nav-item position-relative">
            <router-link class="nav-link py-0 -ps-1 sub-item" to="/settings">
              <span>
                <Settings :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">Settings</span>
              </span>
            </router-link>
          </li>
          <li class="nav-item position-relative">
            <router-link class="nav-link py-0 -ps-1 sub-item" to="/tech/list">
              <span>
                <Users :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">User / Technician Setup</span>
              </span>
            </router-link>
          </li>
          <li class="nav-item position-relative">
            <a class="nav-link py-0 -ps-1 sub-item" href="#" @click.prevent="Emitter.emit('open:departmentModal')">
              <span>
                <Network :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">Departments</span>
              </span>
            </a>
          </li>
          <li class="nav-item position-relative">
            <router-link class="nav-link py-0 -ps-1 sub-item" to="/gps-tracker">
              <span>
                <MapPin :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">GPS Tracker</span>
              </span>
            </router-link>
          </li>
          <li class="nav-item position-relative">
            <router-link class="nav-link py-0 -ps-1 sub-item" to="/admin-reports">
              <span>
                <BarChart2 :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">Admin Reports</span>
              </span>
            </router-link>
          </li>
          <li class="nav-item position-relative">
            <router-link class="nav-link py-0 -ps-1 sub-item" to="/resource-manager">
              <span>
                <FolderOpen :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">Resource Manager</span>
              </span>
            </router-link>
          </li>
        </ul>
      </li>

      <!-- Customer Portal Accordion -->
      <li class="nav-item position-relative">
        <a class="nav-link py-0 -ps-1 accordion-toggle" href="#" @click.prevent="toggleAccordion('portal')">
          <span>
            <Globe :size="20" class="position-absolute mt-1" />
            <span class="d-inline-block ms-4 ps-2">Customer Portal</span>
            <ChevronRight :size="16" class="accordion-chevron" :class="{ 'rotated': openAccordion === 'portal' }" />
          </span>
        </a>
        <ul class="nav flex-column accordion-content" :class="{ 'open': openAccordion === 'portal' }">
          <li class="nav-item position-relative">
            <a class="nav-link py-0 -ps-1 sub-item disabled-link" href="#" @click.prevent>
              <span>
                <ListOrdered :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">Request Management</span>
              </span>
            </a>
          </li>
          <li class="nav-item position-relative">
            <a class="nav-link py-0 -ps-1 sub-item disabled-link" href="#" @click.prevent>
              <span>
                <Layers :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">Project Status</span>
              </span>
            </a>
          </li>
          <li class="nav-item position-relative">
            <a class="nav-link py-0 -ps-1 sub-item disabled-link" href="#" @click.prevent>
              <span>
                <ClipboardCheck :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">Service Reports</span>
              </span>
            </a>
          </li>
          <li class="nav-item position-relative">
            <a class="nav-link py-0 -ps-1 sub-item disabled-link" href="#" @click.prevent>
              <span>
                <FolderOpen :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">Client Documents</span>
              </span>
            </a>
          </li>
        </ul>
      </li>
      
      <li><hr /></li>
      
      <!-- Payroll Accordion -->
      <li class="nav-item position-relative">
        <a class="nav-link py-0 -ps-1 accordion-toggle" href="#" @click.prevent="toggleAccordion('payroll')">
          <span>
            <DollarSign :size="20" class="position-absolute mt-1" />
            <span class="d-inline-block ms-4 ps-2">Payroll</span>
            <ChevronRight :size="16" class="accordion-chevron" :class="{ 'rotated': openAccordion === 'payroll' }" />
          </span>
        </a>
        <ul class="nav flex-column accordion-content" :class="{ 'open': openAccordion === 'payroll' }">
          <li class="nav-item position-relative">
            <router-link class="nav-link py-0 -ps-1 sub-item" to="/pto-report">
              <span>
                <Calendar :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">PTO</span>
              </span>
            </router-link>
          </li>
          <li class="nav-item position-relative">
            <router-link class="nav-link py-0 -ps-1 sub-item" to="/timesheets">
              <span>
                <Clock :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">Timesheets</span>
              </span>
            </router-link>
          </li>
          <li class="nav-item position-relative">
            <router-link class="nav-link py-0 -ps-1 sub-item" to="/expense/new">
              <span>
                <Banknote :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">Expense Submittal</span>
              </span>
            </router-link>
          </li>
          <li class="nav-item position-relative">
            <a class="nav-link py-0 -ps-1 sub-item disabled-link" href="#" @click.prevent>
              <span>
                <FileText :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">W2</span>
              </span>
            </a>
          </li>
        </ul>
      </li>

      <!-- Training Accordion -->
      <li class="nav-item position-relative">
        <a class="nav-link py-0 -ps-1 accordion-toggle" href="#" @click.prevent="toggleAccordion('training')">
          <span>
            <GraduationCap :size="20" class="position-absolute mt-1" />
            <span class="d-inline-block ms-4 ps-2">Training</span>
            <ChevronRight :size="16" class="accordion-chevron" :class="{ 'rotated': openAccordion === 'training' }" />
          </span>
        </a>
        <ul class="nav flex-column accordion-content" :class="{ 'open': openAccordion === 'training' }">
          <li class="nav-item position-relative">
            <a class="nav-link py-0 -ps-1 sub-item disabled-link" href="#" @click.prevent>
              <span>
                <BookOpen :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">Training Records</span>
              </span>
            </a>
          </li>
          <li class="nav-item position-relative">
            <a class="nav-link py-0 -ps-1 sub-item disabled-link" href="#" @click.prevent>
              <span>
                <Calendar :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">Schedule Training</span>
              </span>
            </a>
          </li>
        </ul>
      </li>

      <!-- Equipment Accordion -->
      <li class="nav-item position-relative">
        <a class="nav-link py-0 -ps-1 accordion-toggle" href="#" @click.prevent="toggleAccordion('equipment')">
          <span>
            <HardHat :size="20" class="position-absolute mt-1" />
            <span class="d-inline-block ms-4 ps-2">Equipment</span>
            <ChevronRight :size="16" class="accordion-chevron" :class="{ 'rotated': openAccordion === 'equipment' }" />
          </span>
        </a>
        <ul class="nav flex-column accordion-content" :class="{ 'open': openAccordion === 'equipment' }">
          <li class="nav-item position-relative">
            <a class="nav-link py-0 -ps-1 sub-item disabled-link" href="#" @click.prevent>
              <span>
                <ClipboardCheck :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">Inspection Records</span>
              </span>
            </a>
          </li>
          <li class="nav-item position-relative">
            <router-link class="nav-link py-0 -ps-1 sub-item" to="/forms">
              <span>
                <FileEdit :size="18" class="position-absolute mt-1" />
                <span class="d-inline-block ms-4 ps-2">Inspection Forms</span>
              </span>
            </router-link>
          </li>
        </ul>
      </li>

    </ul>
  </div>
  <div>
    <ul class="nav flex-column">
      <li class="nav-item position-relative">
        <router-link class="nav-link py-0 -ps-1" to="/logout">
          <span>
          <LogOut :size="20" class="position-absolute mt-1" />
          <span class="d-inline-block ms-4 ps-2">Logout</span>
          </span>
        </router-link>
      </li>
    </ul>
    <div class="version d-flex align-items-center justify-content-between">
      <span>v{{ appVersion }}</span>
      <a href="#" class="refresh-link" @click.prevent="refreshApp" title="Clear cache and reload">
        <RefreshCw :size="14" /> Refresh
      </a>
    </div>
  </div>
  </nav>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from "vue";
import { RouterLink } from "vue-router";
import Emitter from "@/services/Emitter";
import ApiService from "@/services/ApiService";
import { usePermissions } from "@/composables/usePermissions";
import { useTimeClock } from "@/composables/useTimeClock";
import { useJobStatusStore } from "@/stores/JobStatusStore";
import { 
  PlusCircle, 
  CheckSquare, 
  UserCheck,
  FileText, 
  Building, 
  Users, 
  Calendar, 
  Package, 
  Banknote, 
  Car, 
  Wrench, 
  Clock, 
  LogOut,
  ClipboardList,
  ChevronRight,
  FolderOpen,
  BarChart2,
  Bell,
  FileEdit,
  RefreshCw,
  Settings,
  LayoutDashboard,
  Zap,
  CheckCircle,
  Receipt,
  DollarSign,
  ClipboardCheck,
  ListChecks,
  Headphones,
  ShoppingCart,
  Truck,
  MapPin,
  Layers,
  ListOrdered,
  Network,
  ClipboardPlus,
  MessageSquare,
  Workflow,
  FolderKanban,
  FileStack,
  Globe,
  GraduationCap,
  HardHat,
  Eye,
  Ban,
  TrendingDown,
  Repeat,
  BookOpen
} from 'lucide-vue-next';

const appVersion = __APP_VERSION__;

const isMobile = () => window.innerWidth <= 768;
const showSidebar = ref(!isMobile());
const openAccordion = ref<string | null>(null);
const unreadCount = ref(0);

// Permission checks
const { isAdmin } = usePermissions();

// Time clock state
const { isClockedIn, statusText: clockStatusText, formattedElapsedTime, fetchStatus } = useTimeClock();

// Job statuses - used to resolve Quick View links by status name since the
// status ids are tenant-configured.
const jobStatusStore = useJobStatusStore();

const jobStatusIdByName = computed(() => {
    const map: Record<string, number> = {};
    for (const status of Object.values(jobStatusStore.list || {})) {
        if (status?.Id && status?.Status) {
            map[status.Status.toLowerCase()] = status.Id;
        }
    }
    return map;
});

// Returns a /job/list route filtered to the named job status, or null when the
// tenant has no status with that name (item renders disabled).
const jobStatusLink = (statusName: string) => {
    const id = jobStatusIdByName.value[statusName.toLowerCase()];
    return id ? { path: '/job/list', query: { status: id, page: 0 } } : null;
};

const openClockModal = () => {
  Emitter.emit("open:timeClockModal");
};

const loadUnreadCount = async () => {
  try {
    unreadCount.value = await ApiService.getUnreadNotificationCount(true);
  } catch (error) {
    console.error('Error loading unread count:', error);
  }
};

onMounted(() => {
  loadUnreadCount();
  fetchStatus();
  jobStatusStore.load();
  // Emit initial sidebar state so layout knows if sidebar is visible
  Emitter.emit("sidebar-state", showSidebar.value);
});

Emitter.on("notification:refresh", loadUnreadCount);

const toggleSidebar = () => {
  showSidebar.value = !showSidebar.value;
  Emitter.emit("sidebar-state", showSidebar.value);
};

const toggleAccordion = (section: string) => {
  if (openAccordion.value === section) {
    openAccordion.value = null;
  } else {
    openAccordion.value = section;
  }
};

Emitter.on("sidebar", toggleSidebar);

const openJobModal = () => {
  Emitter.emit("open:requestForm");
};

const openAssignmentModal = () => {
  Emitter.emit("open:taskForm");
};

const openRequestModal = () => {
  Emitter.emit("open:requestForm");
};

const refreshApp = async () => {
  // Clear caches and do a hard refresh
  if ('caches' in window) {
    try {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
      console.log('Caches cleared');
    } catch (e) {
      console.error('Failed to clear caches:', e);
    }
  }
  
  // Clear localStorage app-specific items (preserve auth)
  const keysToKeep = ['token', 'user'];
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && !keysToKeep.includes(key)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(key => localStorage.removeItem(key));
  
  // Hard refresh bypassing cache
  window.location.reload();
};
</script>

<style scoped>

.not-clocked {
  color: rgba(223, 68, 68, 0.801)!important;
}

.clocked:hover *:not(.badge) {
  color: #ffffff !important;
}

.sidebar {
  width: 300px;
  max-width: 300px;
  min-width: 300px;
  background-color: rgb(34,30,31);
  background-color: #132341;
  color: #ffffff;
  height: calc(100vh - 70px);
  position: sticky;
  top: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  z-index:1000;
  transition: transform 0.3s ease-in-out, width 0.3s ease-in-out, min-width 0.3s ease-in-out, max-width 0.3s ease-in-out;
  flex-shrink: 0;
}

.sidebar-visible {
  transform: translateX(0);
}

.sidebar-hidden {
  transform: translateX(-100%);
  width: 0;
  min-width: 0;
  max-width: 0;
  overflow: hidden;
}
.sidebar .nav-link {
  color: #adb5bd;
  /* padding: 0.75rem 1.5rem; */
}

.sidebar .nav-link > span {
  display: block;
  padding: 0.75rem 1.5rem;
  position: relative;
  overflow: hidden;
  /* border-radius: 50px; */
}

/* Hover effect - aquamarine green badge style */
.sidebar .nav-link:hover > span {
  color: #ffffff;
  background-color: #3B9796;
  transition: background-color 0.2s ease;
}

.sidebar .nav-link:hover > span svg {
  color: #ffffff;
}

/* Disabled items - sections that don't have a route yet */
.sidebar .nav-link.disabled-link > span {
  opacity: 0.4;
  cursor: default;
}

.sidebar .nav-link.disabled-link:hover > span {
  color: #adb5bd;
  background-color: transparent;
}

/* Commented out: Glow/shimmer hover effect
.sidebar .nav-link:hover > * {
  color: #ffffff; 
  transition: padding-left 0.4s;
  padding-left: 2rem!important;
}
.sidebar .nav-link:hover > *:not(span) {
  color: #BF092F!important;
}

.sidebar .nav-link:hover > span {
  background-color: #495057;
  box-shadow: inset 2px 2px 5px rgba(0, 0, 0, 0.2);
}

.sidebar .nav-link:hover > span i:last-child {
  display: inline-block!important;
}

.sidebar .nav-link:hover > span::after {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.3) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  animation: shimmer 1.5s ease-in-out;
  pointer-events: none;
}

@keyframes shimmer {
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
}
*/

/* Active page styles - match hover state */
.sidebar .nav-link.router-link-active > span {
  color: #ffffff;
  background-color: #3B9796;
}

.sidebar .nav-link.router-link-active > span svg {
  color: #ffffff;
}

.sidebar .version {
  padding: 0.75rem 1rem;
  color: #6c757d;
  font-size: 0.8rem;
}

.sidebar .version .refresh-link {
  color: #6c757d;
  text-decoration: none;
  font-size: 0.75rem;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  transition: color 0.2s ease;
}

.sidebar .version .refresh-link:hover {
  color: #3B9796;
}

.sidebar-overlay {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index:999;
}

@media (max-width: 768px) {
  .sidebar {
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    z-index: 1001;
  }
}

@media (min-width: 769px) {
  .sidebar-overlay {
    display: none; /* No overlay needed on desktop */
  }
}

/* Accordion styles */
.accordion-toggle > span {
  display: flex;
  align-items: center;
}

.accordion-chevron {
  position: absolute;
  right: 1.5rem;
  transition: transform 0.3s ease;
}

.accordion-chevron.rotated {
  transform: rotate(90deg);
}

.accordion-content {
  max-height: 0;
  overflow: hidden;
  opacity: 0;
  transition: max-height 0.3s ease-out, opacity 0.3s ease-out;
  background-color: rgba(0, 0, 0, 0.25);
  box-shadow: inset 0 4px 8px rgba(0, 0, 0, 0.3);
}

.accordion-content.open {
  max-height: 600px;
  opacity: 1;
  transition: max-height 0.4s ease-in, opacity 0.4s ease-in;
}

.accordion-content .sub-item > span {
  padding-left: 1.5rem !important;
}
</style>
